import React from 'react';
import { Link } from '@inertiajs/react';
import AdminLayout from '../../layouts/AdminLayout';
import DataTable, { type Column } from '../../components/admin/DataTable';
import { day, since } from '../../components/admin/format';

type Credential = {
  kind: string;
  label: string;
  token: string;
  created_at: string;
  used_at: string | null;
  state: 'ok' | 'revoked' | 'expired';
};

type UserProps = {
  user: {
    id: number;
    email: string;
    name: string | null;
    created_at: string;
    daily_task_cap: number;
  };
  last_seen_at: string | null;
  counts: Record<string, number>;
  activity: { at: string; kind: string; title: string; href: string }[];
  credentials: Credential[];
};

type UserComponent = React.FC<UserProps> & {
  layout?: (page: React.ReactNode) => React.ReactNode;
};

// 三个页面各有一份 STATE_PILL，不抽公共组件：三处的状态词汇本来就不同
// （这里是凭证的有效/吊销/过期，内容巡检是活跃/回收站/归档）。抽成一个
// 涵盖全部键的联合类型，等于让每个页面都能取到不属于它的状态。
const STATE_PILL: Record<Credential['state'], [string, string]> = {
  ok: ['有效', 'bg-admin-okSoft text-admin-ok'],
  revoked: ['已吊销', 'bg-admin-dangerSoft text-admin-danger'],
  expired: ['已过期', 'bg-admin-surface2 text-admin-muted'],
};

const COUNT_LABELS: [string, string][] = [
  ['tasks_active', '任务 · 活跃'],
  ['tasks_discarded', '任务 · 回收站'],
  ['projects', '项目'],
  ['pomodoros', '番茄钟'],
  ['time_entries', '计时记录'],
  ['worklogs', 'Worklog'],
  ['wiki_pages', 'Wiki 页面'],
  ['comments', '留言'],
];

const CRED_COLUMNS: Column<Credential>[] = [
  { key: 'kind', header: '类型', width: '86px', render: (c) => c.kind },
  { key: 'label', header: '名称 / 应用', truncate: true, render: (c) => c.label },
  { key: 'token', header: '令牌', width: '190px', mono: true, render: (c) => c.token },
  { key: 'created', header: '创建', width: '96px', render: (c) => day(c.created_at) },
  {
    key: 'used',
    header: '最近使用',
    width: '92px',
    render: (c) => <span className="text-admin-muted">{c.used_at ? since(c.used_at) : '—'}</span>,
  },
  {
    key: 'state',
    header: '状态',
    width: '78px',
    render: (c) => {
      const [label, cls] = STATE_PILL[c.state];
      return <span className={`rounded px-1.5 py-px text-[11px] font-semibold ${cls}`}>{label}</span>;
    },
  },
];

const Card: React.FC<{ title: string; children: React.ReactNode; pad?: boolean }> = ({
  title,
  children,
  pad = true,
}) => (
  <div className="mb-3 rounded-lg border border-admin-line bg-admin-surface">
    <div className="border-b border-admin-line px-3.5 py-2.5 text-[12.5px] font-semibold">{title}</div>
    <div className={pad ? 'p-3.5' : ''}>{children}</div>
  </div>
);

const User: UserComponent = ({ user, last_seen_at, counts, activity, credentials }) => (
  <>
    <h1 className="mb-3 truncate text-[14.5px] font-semibold tracking-tight">
      <span className="font-normal text-admin-muted">用户 / </span>
      {user.email}
    </h1>

    <div className="grid grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] items-start gap-3">
      <div>
        <Card title="基本信息">
          <dl className="grid grid-cols-[88px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[12.5px]">
            <dt className="text-admin-muted">ID</dt>
            <dd className="font-mono">{user.id}</dd>
            <dt className="text-admin-muted">邮箱</dt>
            <dd className="break-all">{user.email}</dd>
            <dt className="text-admin-muted">名称</dt>
            <dd>{user.name ?? '—'}</dd>
            <dt className="text-admin-muted">注册于</dt>
            <dd>{day(user.created_at)}</dd>
            <dt className="text-admin-muted">最近活跃</dt>
            <dd>{since(last_seen_at)}</dd>
            <dt className="text-admin-muted">每日上限</dt>
            <dd>{user.daily_task_cap}</dd>
          </dl>
        </Card>

        <Card title="数据概览" pad={false}>
          <div className="grid grid-cols-[repeat(4,minmax(0,1fr))] gap-px bg-admin-line">
            {COUNT_LABELS.map(([key, label]) => (
              <div key={key} className="bg-admin-surface px-3 py-2.5">
                <div className="truncate text-[11px] text-admin-muted">{label}</div>
                <div className="mt-px text-[15px] font-semibold tabular-nums">{counts[key] ?? 0}</div>
              </div>
            ))}
          </div>
        </Card>

        <div className="rounded-lg border border-admin-line bg-admin-surface">
          <div className="flex items-center border-b border-admin-line px-3.5 py-2.5 text-[12.5px] font-semibold">
            该用户持有的凭证
            <span className="ml-auto text-[11.5px] font-normal text-admin-muted">只读</span>
          </div>
          <DataTable
            columns={CRED_COLUMNS}
            rows={credentials}
            rowKey={(c) => `${c.kind}-${c.token}-${c.created_at}`}
            empty="该用户没有任何凭证"
          />
        </div>
      </div>

      <div>
        <Card title="最近活动">
          {activity.length === 0 && (
            <div className="text-[12.5px] text-admin-muted">还没有任何活动</div>
          )}
          <ul className="relative pl-4 before:absolute before:bottom-1.5 before:left-[3px] before:top-1.5 before:w-px before:bg-admin-line">
            {activity.map((item) => (
              <li key={`${item.kind}-${item.href}-${item.at}`} className="py-1 text-[12.5px]">
                <span className="mr-2 tabular-nums text-[11.5px] text-admin-muted">{day(item.at)}</span>
                <Link href={item.href} className="text-admin-accent hover:underline">
                  {item.title}
                </Link>
              </li>
            ))}
          </ul>
        </Card>

        <div className="rounded-lg border border-dashed border-admin-line2 bg-admin-surface2 px-3 py-2 text-[12px] leading-relaxed text-admin-muted">
          写操作（<b className="font-semibold text-admin-ink2">封禁用户</b>、
          <b className="font-semibold text-admin-ink2">吊销凭证</b>）不在本期。
          两个动作各自需要一处数据变更与一条审计记录，等确定要开时一起做，
          避免先落一个没有审计的写入口。
        </div>
      </div>
    </div>
  </>
);

User.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>;

export default User;
